// src/booking/styles.js

// NOTE: This is basically your existing style objects, moved into one place so
// BookingForm + ManageBooking can share them.

export const styles = {
  // ---------- layout ----------
  page: {
    maxWidth: 980,
    margin: "0 auto",
    padding: 18,
    background: `
      linear-gradient(180deg, #f5eedb 0%, #efe4cb 45%, #e6d7bb 100%),
      repeating-linear-gradient(45deg, rgba(0,0,0,0.015) 0, rgba(0,0,0,0.015) 1px, transparent 1px, transparent 6px)
    `,
    borderRadius: 18,
    boxShadow: "0 30px 60px rgba(0,0,0,0.45)",
  },

  card: {
    borderRadius: 16,
    overflow: "hidden",
    background: "linear-gradient(180deg, rgba(255,255,255,0.97), rgba(248,245,238,0.97))",
    border: "0.5px solid rgba(35,28,18,0.75)",
    boxShadow: "0 16px 34px rgba(0,0,0,0.18), inset 0 0 0 1px rgba(35,28,18,0.30)",
  },

  sectionHeader: {
    padding: "12px 16px",
    borderBottom: "1px solid rgba(25,18,10,0.22)",
    fontWeight: 800,
    letterSpacing: "0.03em",
    backgroundImage:
      "linear-gradient(90deg, rgba(210,194,166,0.95), rgba(235,227,210,0.95)), repeating-linear-gradient(45deg, rgba(0,0,0,0.03) 0, rgba(0,0,0,0.03) 1px, transparent 1px, transparent 6px)",
  },

  // ---------- tones ----------
  tones: {
    lead: {
      bg: "linear-gradient(90deg, rgba(227,222,210,0.92), rgba(243,239,230,0.92))",
      border: "#b6ae9b",
      icon: "📝",
      tag: "Lead",
    },
    people: {
      bg: "linear-gradient(90deg, rgba(214,224,210,0.92), rgba(236,242,234,0.92))",
      border: "#9fb19a",
      icon: "👥",
      tag: "People",
    },
    days: {
      bg: "linear-gradient(90deg, rgba(226,214,191,0.92), rgba(243,236,224,0.92))",
      border: "#b49f78",
      icon: "📅",
      tag: "Days",
    },
    meals: {
      bg: "linear-gradient(90deg, rgba(210,194,166,0.92), rgba(235,227,210,0.92))",
      border: "#a68b63",
      icon: "🍽️",
      tag: "Meals",
    },
    accom: {
      bg: "linear-gradient(90deg, rgba(201,214,204,0.92), rgba(232,240,234,0.92))",
      border: "#8fa79a",
      icon: "⛺",
      tag: "Stay",
    },
    merch: {
      bg: "linear-gradient(90deg, rgba(218,199,178,0.92), rgba(241,232,220,0.92))",
      border: "#b09275",
      icon: "👕",
      tag: "Merch",
    },
    review: {
      bg: "linear-gradient(90deg, rgba(184,214,200,0.92), rgba(223,242,233,0.92))",
      border: "#6f9f8a",
      icon: "✅",
      tag: "Review",
    },
  },

  // ---------- typography ----------
  h3: { margin: "0 0 10px 0", fontSize: 15, opacity: 0.95 },

  mutedInline: {
    fontSize: 12,
    fontWeight: 500,
    opacity: 0.85,
  },

  headerSummaryPill: {
    fontSize: 12,
    padding: "3px 10px",
    borderRadius: 999,
    border: "1px solid rgba(0,0,0,0.15)",
    background: "rgba(255,255,255,0.55)",
    fontWeight: 800,
    whiteSpace: "nowrap",
  },

  // ---------- grids ----------
  grid2: { display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" },
  grid3: { display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" },
  grid5: { display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(90px, 1fr))" },

  // ---------- inputs / buttons ----------
  input: {
    height: 38,
    borderRadius: 10,
    border: "1px solid rgba(25,18,10,0.25)",
    background: "rgba(255,255,255,0.75)",
    padding: "0 10px",
    outline: "none",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6)",
  },

  btn: {
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid #222",
    cursor: "pointer",
    background: "white",
  },

  btnDisabled: { opacity: 0.5, cursor: "not-allowed" },

  miniBtn: {
    padding: "6px 10px",
    borderRadius: 8,
    border: "1px solid #bbb",
    cursor: "pointer",
    background: "white",
  },

  // ---------- little UI bits ----------
  pill: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: 38,
    borderRadius: 10,
    border: "1px solid #bbb",
    padding: "0 12px",
  },

  pillWrap: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 },

  pill2: {
    fontSize: 12,
    padding: "4px 10px",
    borderRadius: 999,
    background: "#fff",
    border: "1px solid rgba(0,0,0,0.12)",
    color: "rgba(0,0,0,0.75)",
  },

  checkRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    padding: "10px 12px",
    border: "1px solid #ddd",
    borderRadius: 10,
  },

  radioRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    border: "1px solid #ddd",
    borderRadius: 10,
  },

  rowBetween: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    width: "100%",
  },

  priceTag: {
    fontSize: 12,
    padding: "2px 8px",
    borderRadius: 999,
    border: "1px solid #ddd",
    background: "#fafafa",
    whiteSpace: "nowrap",
  },

  // ---------- meals ----------
  dayMealRow: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: 10,
    display: "grid",
    gap: 10,
  },

  dayMealGrid: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  },

  mealBox: {
    border: "1px solid #ddd",
    borderRadius: 10,
    padding: 12,
  },

  mealBoxDisabled: {
    border: "1px dashed #ddd",
    borderRadius: 10,
    padding: 12,
    opacity: 0.7,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 88,
  },

  // ---------- review ----------
  reviewRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: 12,
    alignItems: "center",
  },

  // ---------- page chrome ----------
  pageWrap: {
    position: "relative",
    padding: 22,
    background:
      "radial-gradient(1200px 600px at 50% 0%, rgba(255,255,255,0.10), transparent 55%), linear-gradient(180deg, #2f2f2f 0%, #252525 60%, #1f1f1f 100%)",
    minHeight: "100vh",
  },

  pageWatermark: {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    opacity: 0.0,
    // IMPORTANT: caller should override backgroundImage because it depends on an imported asset
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center 120px",
    backgroundSize: "520px",
    filter: "grayscale(1)",
  },

  backLink: {
    background: "none",
    border: "none",
    padding: "6px 4px",
    marginBottom: 10,
    color: "rgba(255,255,255,0.85)",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    textDecoration: "none",
  },

  brandBar: {
    border: "1px solid rgba(0,0,0,0.15)",
    borderRadius: 14,
    overflow: "hidden",
    marginBottom: 14,
    background: "#fff",
    boxShadow: "0 8px 22px rgba(0,0,0,0.10)",
  },

  brandHero: {
    position: "relative",
    width: "100%",
    background: "#111",
  },

  brandHeroImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },

  cornerLogo: {
    position: "absolute",
    top: 12,
    width: 64,
    height: 64,
    objectFit: "contain",
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.45)",
    background: "rgba(255,255,255,0.72)",
    padding: 6,
  },
};